import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { graphql, useStaticQuery } from 'gatsby';
import { useIsClient } from '@uidotdev/usehooks';
import { Alert, Badge, Button, Column, Icon, List, Row } from '@pella/sash-framework';
import { Image } from '../../../';
import { isValidArray } from '../../../utilities/validations';
import { setLocalStorage, splitArray } from '../../../utilities/functions';
import HoverBannerCard from '../../../third-party/hover/card-banner';

const InspireGrid = ({ totalCount, tiles, tilesToShow, onLoadMoreClick }) => {
	const { show_more_button } = useStaticQuery(query).contentstackCategoryInspirationConfig;
	const isClient = useIsClient();
	const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1279 });
	const isDesktop = useMediaQuery({ minWidth: 1280 });

	const onInspireCardClick = () => {
		if (!isValidArray(tiles)) return null;

		if (typeof window !== 'undefined') {
			let position = {
				offSetX: window.scrollX || 0,
				offSetY: window.scrollY || 0,
			};

			const flattenURLs = tiles.map((item) => item.url);

			const imgDetailData = {
				position,
				slideData: flattenURLs,
				currentUrl: window.location.pathname,
			};

			setLocalStorage('imgDetailData', imgDetailData);
		}
	};

	const renderColumn = (data, colIndex) => {
		const staticImage = data.image?.[0]?.image;
		const fetchedImage = data.imageConnection?.edges?.[0]?.node?.imageConnection?.edges?.[0]?.node;
		const imageWidth = staticImage?.dimension?.width || fetchedImage?.dimension?.width || 0;
		const imageHeight = staticImage?.dimension?.height || fetchedImage?.dimension?.height || 0;
		const layout = imageWidth > imageHeight ? 'landscape' : 'portrait';
		const shopTheLook = data.image?.[0]?.shop_the_look || data.imageConnection?.edges?.[0]?.node?.shop_the_look;

		if (data.card_type === 'rta') {
			return <HoverBannerCard key={colIndex} />;
		}

		return (
			<li
				key={colIndex}
				className='group block decoration-transparent'
			>
				<a
					href={data.url}
					onClick={onInspireCardClick}
					className='group block decoration-transparent'
				>
					<div className={`inspireCard relative overflow-hidden ${layout === 'portrait' ? 'aspect-square' : 'aspect-[1.6]'}`}>
						{(isValidArray(data.tiles?.tileConnection?.edges) || isValidArray(data.tiles?.tile)) && (
							<Badge
								color='white'
								className='inspireCard-shop absolute left-2 top-2 z-2'
								size='sm'
							>
								<Icon
									name='sell'
									size={14}
								/>
								Shop The Look
							</Badge>
						)}

						<Image
							data={{ image: staticImage || fetchedImage, shop_the_look: shopTheLook }}
							params='?auto=pjpg&format=webp&quality=90'
							className='!block h-full w-full !object-cover transition-transform duration-300 group-focus-within:scale-110 group-hover:scale-110'
						/>
					</div>
				</a>
			</li>
		);
	};

	return (
		<div
			className='relative mb-6 md:mb-8 xl:mb-10'
			data-testid='inspire-grid'
		>
			{!isValidArray(tiles) && (
				<Alert
					type='info'
					hideClose
				>
					No images were found.
				</Alert>
			)}

			{isValidArray(tiles) && (
				<div
					className='relative overflow-hidden max-md:!max-h-none'
					style={{ maxHeight: totalCount > tilesToShow ? (tiles.length / 3) * (isClient && isTablet ? 175 : isClient && isDesktop ? 275 : 300) : '' }}
				>
					{totalCount > tilesToShow && <div className='absolute bottom-0 left-0 z-3 h-[192px] w-full bg-gradient-to-t from-white to-transparent md:h-[336px]' />}

					<Row className='gap-y-4'>
						{(isTablet || isDesktop) &&
							splitArray(tiles, 3).map((column, index) => (
								<Column
									md={4}
									key={`column-${index}`}
									className='space-y-4'
								>
									<List
										bullets='none'
										className='m-0 space-y-4 p-0'
									>
										{isValidArray(column) && column.map((data, colIndex) => renderColumn(data, colIndex))}
									</List>
								</Column>
							))}

						{!isTablet &&
							!isDesktop &&
							splitArray(tiles, 2).map((column, index) => (
								<Column
									xs={12}
									sm={6}
									key={`column-${index}`}
									className='space-y-4'
								>
									<List
										bullets='none'
										className='m-0 space-y-4 p-0'
									>
										{isValidArray(column) && column.map((data, colIndex) => renderColumn(data, colIndex))}
									</List>
								</Column>
							))}
					</Row>
				</div>
			)}

			{isValidArray(tiles) && totalCount > tilesToShow && (
				<div className='z-4 mt-4 text-center md:absolute md:bottom-0 md:left-0 md:mt-0 md:w-full'>
					<Button
						color='primary-alt'
						onClick={onLoadMoreClick}
					>
						{show_more_button || 'Load More Images'}
					</Button>
				</div>
			)}
		</div>
	);
};

InspireGrid.propTypes = {
	totalCount: PropTypes.number,
	tiles: PropTypes.array,
	tilesToShow: PropTypes.number,
	onLoadMoreClick: PropTypes.func,
};

export default InspireGrid;

export const query = graphql`
	{
		contentstackCategoryInspirationConfig {
			show_more_button
			rta_card {
				file {
					url
					gatsbyImageData(quality: 90)
				}
				heading
				subheading
				cta {
					...CTA
				}
			}
		}
	}
`;
